import { Button, Input, Select, SelectItem, Slider, Spinner } from '@heroui/react';
import { ArrowLeft, Plus, Save, Trash2, Upload } from 'lucide-react';
import React, { FC, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { PageTitle, PageWrapper } from '../../../components';
import { useHeroToast } from '../../../hooks';
import {
  AdGeneratorSettings,
  AdPalette,
  FALLBACK_SETTINGS,
  TEXT_ROLE_LABELS,
  TEXT_ROLES,
  useGetAdGeneratorSettings,
  useUpdateAdGeneratorSettings,
} from '../../../queries';
import { GENERATOR_ROUTES } from '../components';
import { CardHeading, CardNote, ColorListEditor, FontGrid, FontPreview, PaletteRow, SettingsCard } from '../components/Settings';
import { ensureFontsLoaded, FONT_OPTIONS } from '../config/fonts';
import { useAssetUpload } from '../hooks';
import { useAdsGeneratorStore } from '../model/store';

const MAX_BRAND_COLORS = 18;
const MAX_PALETTE_COLORS = 8;
const MAX_PALETTES = 12;
const MAX_AI_ASSETS = 6;

const newPalette = (index: number): AdPalette => ({
  id: `palette-${Date.now()}-${index}`,
  name: `Paleta ${index + 1}`,
  colors: ['#0f3333', '#c18344'],
});

/**
 * Ustawienia generatora. Trzymane w bazie, bo dotyczą marki, a nie osoby:
 * kolory i kroje mają być takie same niezależnie od tego, kto układa kreację.
 */
export const SettingsPage: FC = () => {
  const navigate = useNavigate();
  const scene = useAdsGeneratorStore((state) => state.scene);
  const { data, isLoading } = useGetAdGeneratorSettings();
  const { mutateAsync: save, isPending } = useUpdateAdGeneratorSettings();
  const { showError, showSuccess } = useHeroToast();

  const [draft, setDraft] = useState<AdGeneratorSettings>(data ?? FALLBACK_SETTINGS);
  const logo = useAssetUpload();

  useEffect(() => {
    ensureFontsLoaded();
  }, []);

  // Szkic startuje od stanu z bazy dopiero, gdy ten dojedzie — inaczej pierwsza
  // odpowiedź nadpisywałaby zmiany wprowadzone w międzyczasie.
  useEffect(() => {
    if (data) setDraft(data);
  }, [data]);

  const patchPalette = (id: string, patch: Partial<AdPalette>): void => {
    setDraft((current) => ({
      ...current,
      palettes: current.palettes.map((palette) => (palette.id === id ? { ...palette, ...patch } : palette)),
    }));
  };

  const submit = async (): Promise<void> => {
    try {
      await save({
        logoUrl: draft.logoUrl,
        brandColors: draft.brandColors,
        palettes: draft.palettes,
        fonts: draft.fonts,
        ai: draft.ai,
      });
      showSuccess('Ustawienia zapisane');
    } catch (error) {
      showError(error instanceof Error ? error.message : 'Nie udało się zapisać ustawień');
    }
  };

  return (
    <PageWrapper>
      <PageTitle
        title="Ustawienia generatora"
        actions={(
          <div className="flex gap-2">
            <Button
              size="sm"
              radius="sm"
              variant="light"
              startContent={<ArrowLeft size={16} />}
              onPress={() => navigate(scene ? GENERATOR_ROUTES.editor : GENERATOR_ROUTES.new)}
            >
              {scene ? 'Wróć do edytora' : 'Wróć do generatora'}
            </Button>
            <Button
              size="sm"
              radius="sm"
              className="bg-black text-white"
              startContent={<Save size={16} />}
              isLoading={isPending}
              onPress={submit}
            >
              Zapisz ustawienia
            </Button>
          </div>
        )}
      />

      {isLoading && <Spinner size="sm" />}

      <div className="flex flex-col gap-4">
        <SettingsCard>
          <div>
            <CardHeading>Kolory marki</CardHeading>
            <CardNote>
              Te kolory pokazują się w każdym próbniku w edytorze i trafiają do AI, gdy wybierzesz kolorystykę marki.
            </CardNote>
          </div>
          <ColorListEditor
            colors={draft.brandColors}
            max={MAX_BRAND_COLORS}
            onChange={(brandColors) => setDraft((current) => ({ ...current, brandColors }))}
          />
        </SettingsCard>

        <SettingsCard>
          <div>
            <CardHeading>Logo marki</CardHeading>
            <CardNote>
              Trafia do elementów z logo w edytorze. Bez wgranego pliku używamy sygnetu Hotelspota z panelu.
              Najlepiej sprawdza się PNG albo SVG z przezroczystym tłem.
            </CardNote>
          </div>
          <input
            ref={logo.inputRef}
            type="file"
            accept={logo.accept}
            hidden
            onChange={async (event) => {
              const uploaded = await logo.handleFiles(event.target.files);
              if (uploaded[0]) setDraft((current) => ({ ...current, logoUrl: uploaded[0].url }));
            }}
          />
          <div className="flex items-center gap-3">
            {Boolean(draft.logoUrl) && (
              <img
                src={draft.logoUrl}
                alt=""
                crossOrigin="anonymous"
                className="h-14 w-14 rounded-lg border border-gray-200 bg-gray-50 object-contain p-1"
              />
            )}
            <Button
              size="sm"
              radius="sm"
              variant="flat"
              startContent={<Upload size={16} />}
              isLoading={logo.isUploading}
              onPress={logo.openPicker}
            >
              {draft.logoUrl ? 'Zmień logo' : 'Wgraj logo'}
            </Button>
            {Boolean(draft.logoUrl) && (
              <Button
                size="sm"
                radius="sm"
                variant="light"
                onPress={() => setDraft((current) => ({ ...current, logoUrl: '' }))}
              >
                Wróć do sygnetu Hotelspota
              </Button>
            )}
          </div>
        </SettingsCard>

        <SettingsCard>
          <div className="flex items-start justify-between gap-3">
            <div>
              <CardHeading>Palety kolorystyczne</CardHeading>
              <CardNote>Zestawy pod konkretne kampanie. Wybierzesz je z próbnika w edytorze.</CardNote>
            </div>
            <Button
              size="sm"
              radius="sm"
              variant="flat"
              startContent={<Plus size={16} />}
              isDisabled={draft.palettes.length >= MAX_PALETTES}
              onPress={() => setDraft((current) => ({
                ...current,
                palettes: [...current.palettes, newPalette(current.palettes.length)],
              }))}
            >
              Dodaj paletę
            </Button>
          </div>

          {draft.palettes.map((palette) => (
            <PaletteRow key={palette.id}>
              <div className="flex items-center gap-2">
                <Input
                  size="sm"
                  radius="sm"
                  aria-label="Nazwa palety"
                  value={palette.name}
                  maxLength={40}
                  onValueChange={(name) => patchPalette(palette.id, { name })}
                />
                <Button
                  size="sm"
                  radius="sm"
                  isIconOnly
                  variant="light"
                  color="danger"
                  aria-label="Usuń paletę"
                  onPress={() => setDraft((current) => ({
                    ...current,
                    palettes: current.palettes.filter((item) => item.id !== palette.id),
                  }))}
                >
                  <Trash2 size={14} />
                </Button>
              </div>
              <ColorListEditor
                colors={palette.colors}
                max={MAX_PALETTE_COLORS}
                onChange={(colors) => patchPalette(palette.id, { colors })}
              />
            </PaletteRow>
          ))}
        </SettingsCard>

        <SettingsCard>
          <div>
            <CardHeading>Kroje pisma</CardHeading>
            <CardNote>
              Krój przypisany do rodzaju tekstu. Dotyczy elementów wstawianych z panelu Tekst; szablony trzymają własną typografię.
            </CardNote>
          </div>
          <FontGrid>
            {TEXT_ROLES.map((role) => (
              <div key={role}>
                <Select
                  size="sm"
                  radius="sm"
                  label={TEXT_ROLE_LABELS[role]}
                  labelPlacement="outside"
                  aria-label={TEXT_ROLE_LABELS[role]}
                  selectedKeys={new Set([draft.fonts[role]])}
                  onSelectionChange={(keys) => {
                    const [next] = Array.from(keys as Set<string>);
                    if (next) {
                      setDraft((current) => ({ ...current, fonts: { ...current.fonts, [role]: next } }));
                    }
                  }}
                >
                  {FONT_OPTIONS.map((option) => (
                    <SelectItem key={option.family} textValue={option.label}>
                      {option.label}
                    </SelectItem>
                  ))}
                </Select>
                <FontPreview $family={draft.fonts[role]}>Gość zamawia z pokoju</FontPreview>
              </div>
            ))}
          </FontGrid>
        </SettingsCard>

        <SettingsCard>
          <div>
            <CardHeading>Generowanie z AI</CardHeading>
            <CardNote>
              Ile zdjęć wolno oddać modelowi jako materiał. Więcej zdjęć to dłuższe rozumowanie i bardziej
              zatłoczony układ, dlatego serwer i tak nie przepuści więcej niż
              {' '}
              {MAX_AI_ASSETS}
              .
            </CardNote>
          </div>
          <Slider
            size="sm"
            label="Maksymalna liczba zdjęć"
            minValue={1}
            maxValue={MAX_AI_ASSETS}
            step={1}
            value={draft.ai.maxAssets}
            onChange={(value) => setDraft((current) => ({
              ...current,
              ai: { maxAssets: Array.isArray(value) ? value[0] : value },
            }))}
          />
        </SettingsCard>
      </div>
    </PageWrapper>
  );
};
